import { ScoringType } from 'components/Workout/ScoringTypes';
import { LogWorkoutResultInput } from 'hooks/workoutResult/useLogWorkoutResult';
import { WorkoutResultSetWrite, WorkoutSectionRetrieve } from 'types';

import {
  DistanceUnit,
  MovementResult,
  ResultsState,
  WeightUnit,
  WorkoutMovementWithScoring,
  WorkoutResultWithSets,
  WorkoutSectionWithScoring,
  createEmptyMovementResult,
} from './types';

// Mantine NumberInput emits `''` when the field is empty; the backend expects `null`.
const toNullableNumber = (v: number | ''): number | null => (v === '' ? null : v);

// Both time variants collapse to TIME since the input is the same.
export const SCORE_TYPE_MAP: Record<string, ScoringType> = {
  calories: ScoringType.CALORIES,
  distance: ScoringType.DISTANCE,
  reps: ScoringType.REPS,
  rounds: ScoringType.ROUNDS,
  rounds_and_reps: ScoringType.ROUNDS_REPS,
  time_higher_better: ScoringType.TIME,
  time_lower_better: ScoringType.TIME,
  weight_sets_reps: ScoringType.WEIGHT,
};

export const enrichSections = (sections: WorkoutSectionRetrieve[]): WorkoutSectionWithScoring[] =>
  sections.map(section => ({
    ...section,
    movements: section.movements.map(m => ({
      ...m,
      setCount: m.scoreSets.length,
      scoringType: SCORE_TYPE_MAP[m.scoreType],
    })),
  }));

export const hydrateMovementResult = (
  apiResult: WorkoutResultWithSets,
  movement: WorkoutMovementWithScoring,
): MovementResult => {
  const { scoringType } = movement;
  switch (scoringType) {
    case ScoringType.TIME:
      return { scoringType, minutes: apiResult.timeMinutes ?? '', seconds: apiResult.timeSeconds ?? '' };
    case ScoringType.CALORIES:
      return { scoringType, calories: apiResult.calories ?? '' };
    case ScoringType.DISTANCE: {
      const unit: DistanceUnit =
        movement.distanceUnit === 'km' ? 'km' : movement.distanceUnit === 'mi' ? 'mi' : 'm';
      return {
        scoringType,
        value: apiResult.distanceValue !== null ? parseFloat(apiResult.distanceValue!) : '',
        unit,
      };
    }
    case ScoringType.REPS:
      return { scoringType, reps: apiResult.reps ?? '' };
    case ScoringType.ROUNDS:
      return { scoringType, rounds: apiResult.rounds ?? '' };
    case ScoringType.ROUNDS_REPS:
      return { scoringType, rounds: apiResult.rounds ?? '', reps: apiResult.extraReps ?? '' };
    case ScoringType.WEIGHT: {
      const unit: WeightUnit = movement.weightUnit === 'kg' ? 'kg' : 'lbs';
      const setCount = movement.setCount ?? 0;
      if (apiResult.sets && apiResult.sets.length > 0) {
        // Always render all configured set slots; fill saved ones by setIndex.
        const sets = Array.from({ length: setCount }, (_, i) => {
          const saved = apiResult.sets!.find(s => s.setIndex === i);
          return saved
            ? { reps: saved.repsCompleted, weight: parseFloat(saved.weightValue), unit }
            : {
                reps: movement.scoreSets[i]?.targetReps ?? ('' as const),
                weight: '' as const,
                unit,
              };
        });
        return { scoringType, sets };
      }
      return createEmptyMovementResult(movement);
    }
  }
};

export const buildInitialResults = (
  sections: WorkoutSectionWithScoring[],
  apiResults: WorkoutResultWithSets[] = [],
): ResultsState => {
  const state: ResultsState = {};
  sections.forEach(section => {
    section.movements.forEach(movement => {
      const apiResult = apiResults.find(
        r => r.workoutSectionMovementId === movement.workoutSectionMovementId,
      );
      state[movement.workoutSectionMovementId] = apiResult
        ? hydrateMovementResult(apiResult, movement)
        : createEmptyMovementResult(movement);
    });
  });
  return state;
};

export const buildResultPayload = (
  movementId: string,
  result: MovementResult,
  athleteKiloClassId: string | undefined,
): LogWorkoutResultInput => {
  const base = { workoutSectionMovement: movementId, athleteKiloClass: athleteKiloClassId ?? null };
  switch (result.scoringType) {
    case ScoringType.TIME: {
      // Backend requires both time_minutes and time_seconds. Coerce empty → 0 when
      // at least one field is filled so partial-fill UX (typed "5" minutes only)
      // saves as 5:00 instead of erroring.
      const minutes = toNullableNumber(result.minutes);
      const seconds = toNullableNumber(result.seconds);
      const hasAnyValue = minutes !== null || seconds !== null;
      return {
        ...base,
        timeMinutes: hasAnyValue ? minutes ?? 0 : null,
        timeSeconds: hasAnyValue ? seconds ?? 0 : null,
      };
    }
    case ScoringType.CALORIES:
      return { ...base, calories: toNullableNumber(result.calories) };
    case ScoringType.DISTANCE: {
      // Backend stores distance as a Decimal — serialize as string to match the API contract.
      const distance = toNullableNumber(result.value);
      return { ...base, distanceValue: distance === null ? null : String(distance) };
    }
    case ScoringType.REPS:
      return { ...base, reps: toNullableNumber(result.reps) };
    case ScoringType.ROUNDS:
      return { ...base, rounds: toNullableNumber(result.rounds) };
    case ScoringType.ROUNDS_REPS: {
      // Backend requires both rounds and extra_reps. Coerce empty → 0 same as TIME.
      const rounds = toNullableNumber(result.rounds);
      const extraReps = toNullableNumber(result.reps);
      const hasAnyValue = rounds !== null || extraReps !== null;
      return {
        ...base,
        rounds: hasAnyValue ? rounds ?? 0 : null,
        extraReps: hasAnyValue ? extraReps ?? 0 : null,
      };
    }
    case ScoringType.WEIGHT:
      return {
        ...base,
        sets: result.sets
          .filter(
            (s): s is { reps: number; weight: number; unit: WeightUnit } =>
              typeof s.reps === 'number' && typeof s.weight === 'number',
          )
          .map(
            (s, i): WorkoutResultSetWrite => ({
              setIndex: i,
              repsCompleted: s.reps,
              weightValue: String(s.weight),
            }),
          ),
      };
  }
};
